import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, Leaf, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT_PAGE } from "@/constants/testIds";
import { RESTAURANT } from "@/data/menu";
import Reveal from "@/components/site/Reveal";
import axios from "axios";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const CONTACT_ENDPOINT = "https://giridharveg.com/contact.php";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a message.");
      return;
    }
    try {
      await axios.post(CONTACT_ENDPOINT, form);
      setSent(true);
      toast.success("Thanks! We'll get back to you shortly.");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setSent(false), 4500);
    } catch (err) {
      console.error("Contact form error:", err);
      toast.error("Something went wrong. Please try again or call us directly.");
    }
  };

  const mapsEmbed = `https://www.google.com/maps?q=${RESTAURANT.mapsLat},${RESTAURANT.mapsLng}&z=17&hl=en&output=embed`;
  const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${RESTAURANT.mapsLat},${RESTAURANT.mapsLng}&destination_place_id=${RESTAURANT.mapsPlaceId}`;

  return (
    <div data-testid={CONTACT_PAGE.page}>
      {/* Header */}
      <section className="container-x pt-16 sm:pt-20 pb-10 mt-24">
        <Reveal>
          <p className="text-xs tracking-[0.24em] uppercase text-brand font-medium">
            Visit & Connect
          </p>
          <h1 className="mt-4 font-serif text-5xl sm:text-6xl text-ink leading-[1.05] max-w-3xl text-balance">
            Come for the food.
            <br />
            <span className="italic">Stay for the warmth.</span>
          </h1>
          <p className="mt-5 text-ink-muted max-w-2xl leading-relaxed">
            We're tucked into the lively heart of Aundh — find us, call us, or
            simply walk in. The kettle is always on.
          </p>
        </Reveal>
      </section>

      {/* Details + Form */}
      <section className="container-x pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Left: Contact details */}
        <Reveal className="lg:col-span-5 space-y-6">
          <div
            data-testid={CONTACT_PAGE.address}
            className="bg-white rounded-3xl border border-borderc/70 p-7 shadow-sm"
          >
            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-brand/10 text-brand shrink-0">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-ink-muted">Address</p>
                <p className="mt-1.5 font-serif text-lg text-ink leading-snug">
                  {RESTAURANT.address}
                </p>
                <a
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={CONTACT_PAGE.directionsBtn}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand hover:text-brand-hover font-medium"
                >
                  Get directions →
                </a>
              </div>
            </div>
          </div>

          <div
            data-testid={CONTACT_PAGE.phone}
            className="bg-white rounded-3xl border border-borderc/70 p-7 shadow-sm"
          >
            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-brand/10 text-brand shrink-0">
                <Phone className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-ink-muted">Phone</p>
                <a
                  href={`tel:${RESTAURANT.phoneRaw}`}
                  data-testid={CONTACT_PAGE.callBtn}
                  className="mt-1.5 block font-serif text-lg text-ink hover:text-brand transition-colors"
                >
                  {RESTAURANT.phone}
                </a>
                <p className="mt-2 text-sm text-ink-muted">
                  Call ahead for takeaway and large group bookings.
                </p>
              </div>
            </div>
          </div>

          <div
            data-testid={CONTACT_PAGE.email}
            className="bg-white rounded-3xl border border-borderc/70 p-7 shadow-sm"
          >
            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-brand/10 text-brand shrink-0">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-ink-muted">Email</p>
                <a
                  href={`mailto:${RESTAURANT.email}`}
                  className="mt-1.5 block font-serif text-lg text-ink hover:text-brand transition-colors break-all"
                >
                  {RESTAURANT.email}
                </a>
              </div>
            </div>
          </div>

          <div
            data-testid={CONTACT_PAGE.hours}
            className="bg-white rounded-3xl border border-borderc/70 p-7 shadow-sm"
          >
            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-brand/10 text-brand shrink-0">
                <Clock className="w-5 h-5" />
              </span>
              <div className="w-full">
                <p className="text-xs uppercase tracking-[0.2em] text-ink-muted">Hours</p>
                <ul className="mt-2 space-y-1.5">
                  {RESTAURANT.hours.map((h) => (
                    <li
                      key={h.day}
                      className="flex items-baseline justify-between gap-4 text-base"
                    >
                      <span className="text-ink">{h.day}</span>
                      <span className="text-ink-muted">{h.time}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-ink-muted inline-flex items-center gap-1.5">
                  <Leaf className="w-3.5 h-3.5 text-brand" /> Open all seven days
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Right: Form */}
        <Reveal delay={150} className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            className="relative bg-white rounded-3xl border border-borderc/70 p-7 sm:p-10 shadow-sm"
          >
            <h2 className="font-serif text-3xl sm:text-4xl text-ink leading-tight">
              Send us a message
            </h2>
            <p className="mt-2 text-ink-muted">
              Questions, feedback or a special occasion — we'd love to hear from you.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">
                  Name
                </label>
                <input
                  id="name"
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name"
                  className="w-full rounded-xl border border-borderc bg-cream/40 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/15 outline-none px-4 py-3 text-ink placeholder:text-ink-muted/60 transition"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">
                  Email
                </label>
                <input
                  id="email"
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-borderc bg-cream/40 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/15 outline-none px-4 py-3 text-ink placeholder:text-ink-muted/60 transition"
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2">
                Message
              </label>
              <textarea
                id="message"
                data-testid="contact-input-message"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us a little about what you have in mind…"
                className="w-full rounded-xl border border-borderc bg-cream/40 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/15 outline-none px-4 py-3 text-ink placeholder:text-ink-muted/60 transition resize-none"
              />
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover text-cream px-7 py-3.5 text-sm font-medium tracking-wide transition-colors"
              >
                {sent ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Sent
                  </>
                ) : (
                  <>
                    Send Message <Send className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-xs text-ink-muted">
                We typically respond within a day.
              </p>
            </div>
          </form>
        </Reveal>
      </section>

      {/* Map */}
      <section className="container-x pb-24">
        <Reveal>
          <div className="overflow-hidden rounded-3xl border border-borderc/70 shadow-sm bg-white">
            <iframe
              title="Giridhar Veg Restaurant — Pune, Maharashtra"
              src={mapsEmbed}
              width="100%"
              height="460"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
